/**
 * Global free-gift sync for the True Greens Pouch.
 *
 * The product page (sections/main-product.liquid) already keeps the free
 * Jar + Scoop gifts in sync while the customer is ON the PDP. But the cart
 * drawer and the /cart page appear on EVERY page of the site, so a customer
 * can remove the main product from the cart while not on the PDP — e.g. after
 * returning from the checkout page. When that happens the PDP script isn't
 * loaded, the gifts are never removed, and (because the "Buy X Get Y"
 * automatic discount no longer has the main product to trigger on) they
 * reappear at full price.
 *
 * This script runs on every page and enforces one invariant, driven by cart
 * state:
 *
 *     cart contains the gifts  <=>  main product is in the cart as a subscription
 *
 * There are two mechanisms, mirroring the working PDP logic:
 *   1. Immediate remove (fast path): when the main product's remove button is
 *      clicked, remove the pouch AND the gifts in ONE request and repaint once,
 *      so nothing lingers and there's no extra round-trip.
 *   2. Reconcile (fallback): a cart-mutation watcher + page-load/back-forward
 *      check that cleans up any orphaned gifts from flows we don't intercept.
 *
 * Repainting uses the same section-innerHTML swap the theme uses on add-to-cart
 * (no force-open, no node replaceWith), so the drawer looks identical to a fresh
 * load. On the /cart page we let the native cart element repaint itself.
 *
 * On the PDP the existing in-page logic owns all of this, so we stand down
 * there (window.FREE_GIFT_PDP_ACTIVE, set in sections/main-product.liquid).
 *
 * Config comes from window.FREE_GIFT_CONFIG (rendered in layout/theme.liquid).
 */
(function () {
  var CONFIG = window.FREE_GIFT_CONFIG;
  if (!CONFIG || !CONFIG.productId || !CONFIG.gifts) return; // feature not configured
  if (window.FREE_GIFT_PDP_ACTIVE) return; // the PDP script owns sync on the product page
  if (!window.routes || !window.routes.cart_add_url) return; // theme routes unavailable

  var CART_RE = /\/cart\/(add|change|update|clear)(\.js)?/i;
  var reconciling = false;

  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then(function (r) {
      return r.json();
    });
  }

  // A cart line is one of OUR free gifts only if it is a gift variant AND carries
  // the tag we add when auto-adding it. A jar/scoop the customer bought on its own
  // has no tag, so it is never added or removed by this script.
  function isTaggedGiftLine(item) {
    return (
      CONFIG.gifts.indexOf(item.variant_id) !== -1 &&
      item.properties &&
      item.properties._free_gift
    );
  }

  // The cart UI element that knows which sections to render (drawer / notification).
  function getCartUI() {
    return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
  }

  function sectionIdsToRequest() {
    var cart = getCartUI();
    if (cart && cart.getSectionsToRender) {
      return cart.getSectionsToRender().map(function (s) {
        return s.id;
      });
    }
    return [];
  }

  // Repaint the drawer / notification from a cart response, WITHOUT the add-flow
  // side effects (Dawn's renderContents force-opens the drawer and can break the
  // empty state). Returns true if it repainted, false if there was nothing to
  // repaint here (e.g. no drawer/notification on this page).
  function renderCart(cartState) {
    var cart = getCartUI();
    if (!cart || !cartState || !cartState.sections || !cart.getSectionsToRender) return false;

    var isEmpty = cartState.item_count === 0;
    var drawer = document.querySelector('cart-drawer');
    if (drawer) {
      drawer.classList.toggle('is-empty', isEmpty);
      var inner = drawer.querySelector('.drawer__inner');
      if (inner) inner.classList.toggle('is-empty', isEmpty);
    }

    cart.getSectionsToRender().forEach(function (section) {
      var target = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      var html = cartState.sections[section.id];
      if (!target || !html) return;
      var source;
      try {
        source = new DOMParser()
          .parseFromString(html, 'text/html')
          .querySelector(section.selector || '.shopify-section');
      } catch (e) {}
      if (source) target.innerHTML = source.innerHTML;
    });
    return true;
  }

  // Ask the theme's own cart element to repaint (used on the /cart page and as a
  // fallback). Both cart-items and cart-drawer-items subscribe to this event.
  function notifyThemeCartUpdated(cartData) {
    try {
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'free-gift-sync', cartData: cartData });
      }
    } catch (e) {
      /* pub/sub unavailable; the next full load will reconcile */
    }
  }

  function renderAfterChange(cartState) {
    // On the /cart page, let the native cart-items element repaint itself.
    if (document.querySelector('cart-items')) {
      notifyThemeCartUpdated(cartState);
      return;
    }
    if (!renderCart(cartState)) notifyThemeCartUpdated(cartState);
  }

  // ---- Fast path: remove main + gifts together in a single request ----------
  document.addEventListener(
    'click',
    function (evt) {
      var removeBtn = evt.target.closest('cart-remove-button');
      if (!removeBtn) return;
      var vidEl = removeBtn.querySelector('[data-variant-id]');
      var variantId = vidEl ? Number(vidEl.dataset.variantId) : 0;
      if (!variantId || !CONFIG.mainVariants || CONFIG.mainVariants.indexOf(variantId) === -1) return;
      var lineIndex = Number(removeBtn.dataset.index) || 0; // 1-based cart line

      // Take over this removal: remove ONLY the clicked pouch line, and take the
      // gifts with it only if no other subscription pouch remains in the cart.
      evt.preventDefault();
      evt.stopImmediatePropagation();

      reconciling = true; // we render ourselves; suppress the watcher

      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) {
          return r.json();
        })
        .then(function (cart) {
          var items = cart.items || [];
          // Which line was clicked (fall back to the first line of that variant).
          var clickedIdx = lineIndex ? lineIndex - 1 : -1;
          if (clickedIdx < 0 || !items[clickedIdx] || items[clickedIdx].variant_id !== variantId) {
            clickedIdx = -1;
            for (var k = 0; k < items.length; k++) {
              if (items[k].variant_id === variantId) {
                clickedIdx = k;
                break;
              }
            }
          }
          if (clickedIdx < 0) return null;

          // Does any OTHER subscription pouch line remain after this removal?
          var subRemains = items.some(function (i, idx) {
            return idx !== clickedIdx && i.product_id === CONFIG.productId && i.selling_plan_allocation;
          });

          // ONE positional /cart/update: zero the clicked line; zero the tagged
          // gifts only when no subscription pouch is left; keep everything else.
          var updates = items.map(function (i, idx) {
            if (idx === clickedIdx) return 0;
            if (!subRemains && isTaggedGiftLine(i)) return 0;
            return i.quantity;
          });
          return postJSON(window.routes.cart_update_url + '.js', {
            updates: updates,
            sections: sectionIdsToRequest(),
            sections_url: window.location.pathname,
          });
        })
        .then(function (result) {
          if (result) renderAfterChange(result);
        })
        .catch(function (e) {
          console.error('[free-gift-sync] immediate remove failed', e);
        })
        .finally(function () {
          reconciling = false;
        });
    },
    true
  );

  // ---- Fallback: reconcile from cart state ----------------------------------
  function reconcile() {
    if (reconciling) return;
    reconciling = true;

    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        var items = cart.items || [];
        var mainLine = items.filter(function (i) {
          return i.product_id === CONFIG.productId;
        })[0];
        var isSubscription = !!(mainLine && mainLine.selling_plan_allocation);

        // Only consider gift lines WE tagged — never the customer's own standalone
        // jar/scoop purchases (same variant, but no tag).
        var taggedGiftPresent = {};
        var taggedGiftKeys = [];
        items.forEach(function (i) {
          if (isTaggedGiftLine(i)) {
            taggedGiftPresent[i.variant_id] = true;
            taggedGiftKeys.push(i.key);
          }
        });

        var sectionsBody = { sections: sectionIdsToRequest(), sections_url: window.location.pathname };

        if (isSubscription) {
          var toAdd = CONFIG.gifts
            .filter(function (id) {
              return !taggedGiftPresent[id];
            })
            .map(function (id) {
              return { id: id, quantity: 1, properties: { _free_gift: 'true' } };
            });
          if (!toAdd.length) return null; // gifts already present
          return postJSON(window.routes.cart_add_url + '.js', Object.assign({ items: toAdd }, sectionsBody));
        } else {
          if (!taggedGiftKeys.length) return null; // no tagged gifts to clean up
          // Zero only our tagged gift lines; keep standalone purchases untouched.
          var updates = items.map(function (i) {
            return isTaggedGiftLine(i) ? 0 : i.quantity;
          });
          return postJSON(window.routes.cart_update_url + '.js', Object.assign({ updates: updates }, sectionsBody));
        }
      })
      .then(function (result) {
        if (result) renderAfterChange(result);
      })
      .catch(function (e) {
        console.error('[free-gift-sync] reconcile failed', e);
      })
      .finally(function () {
        reconciling = false;
      });
  }

  // Debounce: a burst of cart calls should only trigger one reconcile.
  var pending = null;
  function scheduleReconcile() {
    if (pending) return;
    pending = setTimeout(function () {
      pending = null;
      reconcile();
    }, 80);
  }

  // Watch every cart mutation (fetch + XHR) for flows we don't intercept above
  // (subscription toggles, Recharge, quick-add, etc.).
  var origFetch = window.fetch;
  window.fetch = function (input) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var promise = origFetch.apply(this, arguments);
    if (CART_RE.test(url)) {
      promise.then(function () { scheduleReconcile(); }).catch(function () {});
    }
    return promise;
  };

  var xhrOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    this.__fgCartWatch = CART_RE.test(url || '');
    return xhrOpen.apply(this, arguments);
  };
  var xhrSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.send = function () {
    if (this.__fgCartWatch) {
      this.addEventListener('load', function () { scheduleReconcile(); });
    }
    return xhrSend.apply(this, arguments);
  };

  // Clean up orphaned gifts when the cart is first viewed, and when the page is
  // restored from the back/forward cache (e.g. hitting Back from checkout).
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleReconcile);
  } else {
    scheduleReconcile();
  }
  window.addEventListener('pageshow', function (e) {
    if (e.persisted) scheduleReconcile();
  });
})();
